import { Box, FormControl, FormHelperText, OutlinedInput, Stack, Typography, useMediaQuery } from '@mui/material'
import React, { FunctionComponent, ReactElement, ReactNode, SetStateAction } from 'react'
import gaiaTheme from '../../../../root-components/theme'
import { TooltipButton } from '../../miscellaneous-components/tooltip-button'
import { HexColorPicker } from 'react-colorful'
import FormEditButtons from './form-edit-buttons'
import { colorToHex } from '../../../../shared/utils/colors-utils'

export const FormColorInputDisplay: FunctionComponent<{
  isEditPage: boolean,
  allowEdit?: boolean,
  editField?: string | undefined,
  setEditField?: React.Dispatch<SetStateAction<any>> | undefined,
  hideEditIcon?: boolean,
  disabled: boolean,
  title: string,
  name: string,
  type?: 'text' | 'email' | 'number'
  value: string | number | undefined,
  touched?: any,
  errors?: any,
  onChange: any,
  cancelEditHandler?: () => void,
  endAdornment?: ReactElement,
  customErrorMessage?: string,
  tooltip?: string,
  bottomBorder?: boolean,
  topBorder?: boolean,
  sideBorders?: boolean,
  isTopRow?: boolean,
  isBottomRow?: boolean,
  /** When true, label uses semibold weight (e.g. design spec “bold” list labels). */
  titleBold?: boolean,
  /** Rendered immediately after the hex input (same row), before edit icons. */
  afterHexAction?: ReactNode,
}> = ({
  isEditPage,
  allowEdit = true,
  editField,
  setEditField,
  hideEditIcon = false,
  disabled,
  title,
  name,
  type = 'text',
  value,
  touched,
  errors,
  onChange,
  cancelEditHandler,
  endAdornment,
  customErrorMessage,
  tooltip,
  bottomBorder = false,
  topBorder = true,
  sideBorders = true,
  isTopRow = false,
  isBottomRow = false,
  titleBold = false,
  afterHexAction,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    const toggleEditField = (fieldName: string) => {
      if (setEditField) {
        setEditField(fieldName)
      }
    }

    const rawColor = value == null || value === '' ? '' : String(value);
    const hexDisplay = colorToHex(rawColor);
    const pickerSafeHex = /^#[0-9a-fA-F]{6}$/.test(hexDisplay) ? hexDisplay : '#000000';

    return (
      <FormControl fullWidth variant='outlined'>
        <Stack sx={{
          borderWidth: '1px',
          borderBottomStyle: bottomBorder || isBottomRow ? 'solid' : 'none',
          borderColor: 'lightGrey.main',
          borderLeftStyle: sideBorders ? 'solid' : 'none',
          borderRightStyle: sideBorders ? 'solid' : 'none',
          borderTopStyle: topBorder || isTopRow ? 'solid' : 'none',
          pt: 1, pb: 1, pl: 3,
          borderTopLeftRadius: isTopRow ? '10px' : 0,
          borderTopRightRadius: isTopRow ? '10px' : 0,
          borderBottomLeftRadius: isBottomRow ? '10px' : 0,
          borderBottomRightRadius: isBottomRow ? '10px' : 0
        }}>
          <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'center'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
            <Stack sx={{ width: isMobileMode ? '100%' : '40%', minWidth: '100px', color: 'text.primary', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography variant='subtitle1' sx={{ fontWeight: titleBold ? 700 : 400 }}>{title}</Typography>
              {tooltip && !isMobileMode && <TooltipButton
                text={tooltip}
                noPadding={true}
                placement='bottom' />}
            </Stack>
            <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
              <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'>
                {editField !== name && (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      alignSelf: 'center',
                      borderRadius: '4px',
                      backgroundColor: rawColor || 'transparent',
                      border: '1px solid',
                      borderColor:
                        hexDisplay === '#ffffff' || rawColor === 'white'
                          ? 'common.black'
                          : rawColor || 'text.secondary',
                      ml: 3
                    }}
                  />
                )}
                {editField === name &&
                  <Stack sx={{ mr: 1 }}>
                    <HexColorPicker
                      color={pickerSafeHex}
                      onChange={onChange}
                      style={{
                        position: 'relative',
                        display: rawColor || hexDisplay ? 'flex' : 'none',
                        zIndex: 1,
                        textTransform: 'uppercase',
                      }}
                    />
                  </Stack>
                }
                <Box sx={{
                  flex: 1,
                  minWidth: 0,
                  mt: 1,
                  mb: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  gap: afterHexAction != null ? 2 : 0.5,
                  ...(isEditPage
                    ? { ml: 1, mr: 15 }
                    : { ml: 2, mr: 2 }),
                }}>
                  <OutlinedInput id={name} size='small' name={name} type={type}
                    value={hexDisplay}
                    disabled={disabled}
                    onChange={onChange}
                    fullWidth={afterHexAction == null}
                    endAdornment={endAdornment ?? undefined}
                    inputProps={{ min: 0, step: 1 }}
                    error={touched && Boolean(errors)}
                    sx={{
                      ...(afterHexAction != null ? {
                        width: '12ch',
                        minWidth: '12ch',
                        maxWidth: '12ch',
                        flexShrink: 0,
                      } : {}),
                      ...((editField !== name || isEditPage) && {
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                      }),
                      ...((editField === name || !isEditPage) && {
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' }
                      }),
                      color: 'text.secondary'
                    }}></OutlinedInput>
                  {afterHexAction != null ? (
                    <Box sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
                      {afterHexAction}
                    </Box>
                  ) : null}
                </Box>
                {allowEdit &&
                  <FormEditButtons
                    isEditPage={isEditPage}
                    editField={editField}
                    hideEditIcon={hideEditIcon}
                    name={name}
                    toggleEditField={toggleEditField}
                    cancelEditHandler={cancelEditHandler}
                  />}
              </Stack>
              {touched && errors &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errors ? <div>{errors as string}</div> : null}
                </FormHelperText>
              }
              {customErrorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{customErrorMessage}
                </FormHelperText>
              }
            </Stack>
          </Stack>
        </Stack>
      </FormControl>
    )
  }